import React from "react";
import { BrandMark, SgMark } from "./brandMarks";

/**
 * "What you can do": one card per thing the extension does, each with a small drawing of the
 * real UI (sample names, no customer data). Installed users get a "Try it" link per card.
 */
const Icon = ({ children, tone }) => (
  <span className={`ceFeature__icon ceFeature__icon--${tone}`} aria-hidden="true">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  </span>
);

const Bars = ({ widths }) => widths.map((w, i) => <span key={i} className={`ceMini__bar ceMini__bar--w${w}`} />);

const MiniPanel = ({ children }) => (
  <div className="ceMini__panel">
    <div className="ceMini__brand"><SgMark width={7} />salesgear</div>
    {children}
  </div>
);

const Row = ({ kind, children, known }) => (
  <div className="ceMini__row">
    <span className={`ceMini__rowIcon ceMini__rowIcon--${kind}`} aria-hidden="true" />
    <span className="ceMini__rowText">{children}</span>
    {known ? <span className="ceMini__tick" aria-hidden="true" /> : null}
  </div>
);

const MiniBtn = ({ children, ghost }) => <span className={`ceMini__btn${ghost ? " ceMini__btn--ghost" : ""}`}>{children}</span>;

const SCREENS = {
  linkedin: (
    <div className="ceMini">
      <div className="ceMini__page">
        <span className="ceMini__cover" />
        <span className="ceMini__avatar" />
        <b className="ceMini__name">Priya Raman</b>
        <span className="ceMini__sub">VP Sales · Pavilion</span>
        <Bars widths={[80, 60]} />
      </div>
      <MiniPanel>
        <Row kind="email" known>priya@joinpavilion.com</Row>
        <Row kind="phone">+1 512 555 0142</Row>
        <MiniBtn>Add to sequence</MiniBtn>
      </MiniPanel>
    </div>
  ),
  gmail: (
    <div className="ceMini">
      <div className="ceMini__page">
        <span className="ceMini__subject">Re: Pilot next steps</span>
        <div className="ceMini__threadBar">
          <SgMark width={6} />
          <span><b>3 people from acme.com</b> · 1 in Salesgear</span>
          <span className="ceMini__pill">Add 2</span>
        </div>
        <Bars widths={[50, 90, 75, 60]} />
      </div>
    </div>
  ),
  crm: (
    <div className="ceMini">
      <div className="ceMini__page">
        <span className="ceMini__crumb"><BrandMark name="hubspot" size={11} />Contacts / Dana Lee</span>
        <b className="ceMini__name">Dana Lee</b>
        <Bars widths={[70, 55, 65]} />
      </div>
      <MiniPanel>
        <b className="ceMini__name">Dana Lee</b>
        <span className="ceMini__sub">In Q4 Founders · step 2/5</span>
        <Row kind="phone">+1 415 555 0199</Row>
        <div className="ceMini__btns"><MiniBtn>Call</MiniBtn><MiniBtn ghost>Sequence</MiniBtn></div>
        <span className="ceMini__ok">✓ Logs to HubSpot</span>
      </MiniPanel>
    </div>
  ),
  web: (
    <div className="ceMini">
      <div className="ceMini__page">
        <b className="ceMini__headline">Build the GTM team of the future</b>
        <Bars widths={[85, 70]} />
      </div>
      <MiniPanel>
        <b className="ceMini__name">Pavilion</b>
        <span className="ceMini__sub">18 decision makers</span>
        {["Robert M. · CRO", "Sam K. · VP Sales", "Ana L. · Head RevOps"].map((n, i) => (
          <span key={n} className="ceMini__person"><span className={`ceMini__dot ceMini__dot--${i}`} />{n}</span>
        ))}
      </MiniPanel>
    </div>
  ),
  salesnav: (
    <div className="ceMini">
      <div className="ceMini__page">
        <b className="ceMini__name">Q4 Founders · 250 leads</b>
        {[70, 55, 60, 50].map((w, i) => (
          <span key={i} className="ceMini__person"><span className="ceMini__dot" /><span className={`ceMini__bar ceMini__bar--w${w}`} /></span>
        ))}
      </div>
      <MiniPanel>
        <b className="ceMini__name">Import this list</b>
        <span className="ceMini__sub">Into: Q4 Founders</span>
        <MiniBtn>Import 250 leads</MiniBtn>
        <span className="ceMini__progress"><span /></span>
      </MiniPanel>
    </div>
  ),
  hover: (
    <div className="ceMini">
      <div className="ceMini__page ceMini__page--hover">
        <Bars widths={[70]} />
        <span className="ceMini__text">Contact: <u>dana@acme.com</u></span>
        <Bars widths={[60]} />
        <div className="ceMini__hovercard">
          <span className="ceMini__hoverName"><SgMark width={7} /><b>Dana Lee</b><span className="ceMini__tick" /></span>
          <span className="ceMini__sub">In Salesgear · Q4 Founders, step 2/5</span>
          <MiniBtn>Open contact card</MiniBtn>
        </div>
      </div>
    </div>
  ),
};

export const FEATURES = [
  {
    key: "linkedin",
    tone: "linkedin",
    title: "Find email and phone on LinkedIn",
    desc: "Open any profile and reveal a verified email and direct dial in one click.",
    icon: <><rect x="3" y="3" width="18" height="18" rx="3" /><path d="M8 10v7M8 7v.01M12 17v-4a2 2 0 0 1 4 0v4M12 10v7" /></>,
    cta: "Open LinkedIn",
    url: "https://www.linkedin.com/feed/",
  },
  {
    key: "gmail",
    tone: "gmail",
    title: "See who’s who in Gmail",
    desc: "A bar under each thread shows who is in Salesgear, and adds the rest.",
    icon: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    cta: "Open Gmail",
    url: "https://mail.google.com/",
  },
  {
    key: "crm",
    tone: "crm",
    title: "Call and sequence from your CRM",
    desc: "On a HubSpot, Salesforce, Zoho or Pipedrive record: call, add to a sequence, log back to the CRM.",
    icon: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />,
  },
  {
    key: "web",
    tone: "web",
    title: "Find people on any company site",
    desc: "On a company’s website, see its decision makers and find their emails in bulk.",
    icon: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 3 2.5 15 0 18M12 3c-2.5 3-2.5 15 0 18" /></>,
  },
  {
    key: "salesnav",
    tone: "salesnav",
    title: "Import Sales Navigator lists",
    desc: "On a lead list or search, import every lead into a Salesgear list and sequence.",
    icon: <><path d="M4 6h16M4 12h10M4 18h7" /><path d="m17 15 3 3-3 3" /></>,
    cta: "Open Sales Navigator",
    url: "https://www.linkedin.com/sales/",
  },
  {
    key: "hover",
    tone: "hover",
    title: "Hover any email",
    desc: "Hover an email on any site or CRM to see if they are in Salesgear and where.",
    icon: <><path d="M4 4h16v12H8l-4 4z" /><path d="M8 9h8M8 12h5" /></>,
  },
];

const FeatureGrid = ({ installed }) => (
  <div className="ceFeatures">
    {FEATURES.map((f) => (
      <div key={f.key} className="ceFeature">
        <div className="ceFeature__head">
          <Icon tone={f.tone}>{f.icon}</Icon>
          <div className="ceFeature__text">
            <div className="ceFeature__title">{f.title}</div>
            <div className="ceFeature__desc">{f.desc}</div>
          </div>
        </div>
        {SCREENS[f.key]}
        {installed && f.cta ? (
          <a className="ceFeature__try" href={f.url} target="_blank" rel="noopener noreferrer">{f.cta} →</a>
        ) : null}
      </div>
    ))}
  </div>
);

export default FeatureGrid;
